import { useState } from 'react';
import { usePartnerDashboard, usePartnerBalance } from '../hooks/usePartnerDashboard';
import { DashboardCard } from '../components/DashboardCard';
import { CommissionTable } from '../components/CommissionTable';
import { PayoutModal } from '../components/PayoutModal';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@medikei/ui';
import { Spinner } from '@medikei/ui';
import { Wallet, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function PartnerDashboardPage() {
  const { data: dashboard, isLoading, error } = usePartnerDashboard();
  const { data: balance } = usePartnerBalance();
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-600">
          Não foi possível carregar os dados do dashboard.
        </p>
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="mt-4"
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  const displayBalance = balance || dashboard.balance;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">Dashboard</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Acompanhe seu desempenho como afiliado
          </p>
        </div>
        <Button
          onClick={() => setPayoutModalOpen(true)}
          className="gap-2 w-full sm:w-auto shadow-md shadow-primary/20 rounded-xl font-bold h-11"
        >
          <Wallet className="w-4 h-4" />
          Solicitar Saque
        </Button>
      </div>

      <div className="grid gap-3 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <DashboardCard
          title="Saldo Disponível"
          value={displayBalance?.available || 0}
          subtitle="Pronto para saque"
          icon={<Wallet className="w-6 h-6 text-primary" />}
          variant="highlight"
          trend={{ value: 12.5, label: 'este mês' }}
        />
        <DashboardCard
          title="Comissões Pendentes"
          value={displayBalance?.pending || 0}
          subtitle="Aguardando aprovação"
          icon={<TrendingUp className="w-6 h-6 text-amber-500" />}
          variant="default"
        />
        <DashboardCard
          title="Total Ganho"
          value={displayBalance?.total || 0}
          subtitle="Todos os tempos"
          icon={<TrendingUp className="w-6 h-6 text-emerald-500" />}
          variant="success"
          trend={{ value: 8.2, label: 'este mês' }}
        />
        <DashboardCard
          title="Total de Conversões"
          value={dashboard.stats?.totalConversions || 0}
          subtitle="Conversões realizadas"
          icon={<Users className="w-6 h-6 text-blue-500" />}
          trend={{ value: 15.3, label: 'este mês' }}
        />
      </div>

      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-3">
        <Card className="md:col-span-2 rounded-2xl border border-border">
          <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6 pb-2 sm:pb-3">
            <CardTitle className="text-base sm:text-lg font-bold">Taxa de Conversão</CardTitle>
            <Badge variant="primary" size="lg" className="font-bold">
              {dashboard.stats?.conversionRate || 0}%
            </Badge>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 sm:pt-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-full flex-1">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min((dashboard.stats?.conversionRate || 0) * 10, 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                {dashboard.stats?.conversionRate || 0}% das visitas converteram
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-border">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
            <CardTitle className="text-base sm:text-lg font-bold">Estatísticas</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 sm:pt-3">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="font-medium text-muted-foreground">Cliques</span>
                <Badge variant="outline" className="font-semibold">{dashboard.stats?.totalClicks || 0}</Badge>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="font-medium text-muted-foreground">Conversões</span>
                <Badge variant="outline" className="font-semibold">{dashboard.stats?.totalConversions || 0}</Badge>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="font-medium text-muted-foreground">Taxa</span>
                <Badge variant="outline" className="font-semibold">{dashboard.stats?.conversionRate || 0}%</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        <div>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-bold text-foreground">Últimas Comissões</h2>
            <Link
              to="/parceiro/comissoes"
              className="text-xs sm:text-sm font-semibold text-primary hover:underline flex items-center gap-1"
            >
              Ver todas
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <CommissionTable
            commissions={dashboard.recentCommissions || []}
            maxItems={5}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-bold text-foreground">Últimos Pagamentos</h2>
            <Link
              to="/parceiro/saques"
              className="text-xs sm:text-sm font-semibold text-primary hover:underline flex items-center gap-1"
            >
              Ver todos
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-0">
              {!dashboard.recentPayouts || dashboard.recentPayouts.length === 0 ? (
                <div className="py-8 text-center text-xs sm:text-sm text-muted-foreground">
                  Nenhum pagamento realizado ainda
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                  {dashboard.recentPayouts.map((payout) => (
                    <div
                      key={payout.id}
                      className="flex items-center justify-between p-3.5 sm:p-4 gap-2"
                    >
                      <div className="min-w-0">
                        <p className="font-bold text-sm sm:text-base text-foreground">
                          {formatCurrency(payout.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(payout.requestedAt).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <Badge
                        variant={
                          payout.status === 'COMPLETED' ? 'success' :
                          payout.status === 'PENDING' ? 'warning' :
                          payout.status === 'PROCESSING' ? 'info' :
                          'danger'
                        }
                      >
                        {payout.status === 'COMPLETED' && 'Concluído'}
                        {payout.status === 'PENDING' && 'Pendente'}
                        {payout.status === 'PROCESSING' && 'Processando'}
                        {payout.status === 'FAILED' && 'Falhou'}
                        {payout.status === 'CANCELLED' && 'Cancelado'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <PayoutModal
        open={payoutModalOpen}
        onOpenChange={setPayoutModalOpen}
        availableBalance={displayBalance?.available || 0}
        pendingBalance={displayBalance?.pending || 0}
      />
    </div>
  );
}
